import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col, Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FormikRangePicker, FormikInput, FormikNumber, FormikDatePicker, InputSelect } from '../../components';
import { indexBalancesRequest, debounceIndexBalancesRequest } from '../../requests/balances';
import { debounceIndexJobManagementsRequest } from '../../requests/jobManagements';
import { debounceIndexSubsidiariesRequest } from '../../requests/subsidiaries';
import { debounceIndexCostCentersRequest } from '../../requests/costCenters';
import { debounceIndexBranchOfficesRequest } from '../../requests/branchOffices';
import { debounceIndexEmployeesRequest } from '../../requests/employees';

const ALL_SELECTED_LABEL = 'Todos';

const ReliquidationProcessFormContent = ({ errors, touched, setFieldValue, isSubmitting, values }) => {
  const dispatch = useDispatch();
  const { reliquidationProcess } = values;
  const { startDate, endDate } = reliquidationProcess;
  const [concepts, setConcepts] = useState([]);
  const [advanceConcepts, setAdvanceConcepts] = useState([]);

  const resultFetchData = (response, includeCode = false) => {
    const result = response.data.data;
    return result.map(element => ({
      ...element,
      label: includeCode ? `${element.code} - ${element.name}` : element.name,
      value: element.id
    }));
  };

  const initFetchBalances = () => {
    indexBalancesRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false,
        reliquidation: true,
        filter_type: 'asset'
      },
      successCallback: response => {
        const options = resultFetchData(response, true);
        setAdvanceConcepts(options);
      }
    });
  };

  useEffect(initFetchBalances, []);
  useEffect(() => {
    indexBalancesRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false,
        reliquidation: true,
        filter_type: 'asset',
        preloaded: true
      },
      successCallback: response => {
        const options = resultFetchData(response, true);
        setConcepts(options);
      }
    });
  }, [dispatch]);

  const fetchConcepts = (inputValue, callback, addPreloaded = true) => {
    const params = {
      actives: true,
      query: inputValue,
      sort_column: 'name',
      paginate: false,
      reliquidation: true,
      ...(addPreloaded ? { preloaded: true } : {})
    };
    debounceIndexBalancesRequest({
      dispatch,
      params,
      successCallback: response => {
        callback(resultFetchData(response, true));
      }
    });
  };

  const fetchJobManagements = (inputValue, callback) => {
    debounceIndexJobManagementsRequest({
      dispatch,
      params: {
        actives: true,
        query: inputValue,
        sort_column: 'name',
        display_length: 500
      },
      successCallback: data => {
        callback(resultFetchData(data));
      }
    });
  };

  const fetchSubsidiaries = (inputValue, callback) => {
    debounceIndexSubsidiariesRequest({
      dispatch,
      params: {
        actives: true,
        name: inputValue,
        sort_column: 'name',
        display_length: 40
      },
      successCallback: data => {
        callback(resultFetchData(data));
      }
    });
  };

  const fetchCostCenters = (inputValue, callback) => {
    debounceIndexCostCentersRequest({
      dispatch,
      params: {
        actives: true,
        name: inputValue,
        sort_column: 'name',
        display_length: 50
      },
      successCallback: data => {
        callback(resultFetchData(data));
      }
    });
  };

  const fetchBranchOffices = (inputValue, callback) => {
    debounceIndexBranchOfficesRequest({
      dispatch,
      params: {
        actives: true,
        name: inputValue,
        sort_column: 'name',
        display_length: 40
      },
      successCallback: data => {
        callback(resultFetchData(data));
      }
    });
  };

  const fetchEmployees = useCallback(
    (inputValue, callback) => {
      debounceIndexEmployeesRequest({
        dispatch,
        params: {
          filter_name: inputValue,
          sort_column: 'name',
          is_dt: false,
          display_length: 50,
          filterPayrollJobManagements: reliquidationProcess.filterPayrollJobManagementsIds || [],
          filterPayrollSubsidiaries: reliquidationProcess.filterPayrollSubsidiariesIds || [],
          filterPayrollCostCenters: reliquidationProcess.filterPayrollCostCentersIds || [],
          filterPayrollBranchOffices: reliquidationProcess.filterPayrollBranchOfficesIds || []
        },
        successCallback: response => {
          callback([{ id: 'Todos', label: 'Todos', value: 'Todos' }, ...response.data.data]);
        }
      });
    },
    [
      dispatch,
      reliquidationProcess.filterPayrollJobManagementsIds,
      reliquidationProcess.filterPayrollSubsidiariesIds,
      reliquidationProcess.filterPayrollCostCentersIds,
      reliquidationProcess.filterPayrollBranchOfficesIds
    ]
  );

  const employeesSelectKey = useMemo(() => {
    return JSON.stringify({
      jobManagements: reliquidationProcess.filterPayrollJobManagementsIds || [],
      subsidiaries: reliquidationProcess.filterPayrollSubsidiariesIds || [],
      costCenters: reliquidationProcess.filterPayrollCostCentersIds || [],
      branchOffices: reliquidationProcess.filterPayrollBranchOfficesIds || []
    });
  }, [
    reliquidationProcess.filterPayrollJobManagementsIds,
    reliquidationProcess.filterPayrollSubsidiariesIds,
    reliquidationProcess.filterPayrollCostCentersIds,
    reliquidationProcess.filterPayrollBranchOfficesIds
  ]);

  const handleMultiChange = (data, fieldName, allowEmpty = false, clearEmployeeSelection = false) => {
    const newData = data ? data.map(element => element.value) : [];
    if (allowEmpty && !newData.length) newData.push('');
    setFieldValue(fieldName, newData);
    if (clearEmployeeSelection) {
      setFieldValue('reliquidationProcess[employeeIds]', []);
      setFieldValue('reliquidationProcess[employees]', []);
    }
  };

  const hasAllSelection = (groupSelection = []) => {
    return groupSelection.some(option => option.label === ALL_SELECTED_LABEL);
  };

  const handleDisabledSelection = (options, selectedOptions) => {
    if (hasAllSelection(selectedOptions)) {
      return true;
    }
    if (selectedOptions.length === 0) return false;
    return options.label === ALL_SELECTED_LABEL;
  };

  return (
    <Form>
      <Modal.Body>
        <Row>
          <Col className="top-header-green no-border-radius">
            <div className="card-header-title justify-content-center">Datos Generales</div>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={4}>
            <Field name="reliquidationProcess[name]">
              {({ field }) => (
                <FormikInput
                  {...field}
                  abbr
                  label="Nombre del Proceso"
                  placeholder="Ingrese nombre del proceso"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="reliquidationProcess[rangeDate]">
              {({ field }) => (
                <FormikRangePicker
                  name={field.name}
                  abbr
                  startDateName="reliquidationProcess[startDate]"
                  endDateName="reliquidationProcess[endDate]"
                  startDate={startDate}
                  endDate={endDate}
                  labelLeft="Desde"
                  labelRight="Hasta"
                  showClearDates
                  error={
                    getIn(errors, 'reliquidationProcess[startDate]') || getIn(errors, 'reliquidationProcess[endDate]')
                  }
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="reliquidationProcess[paymentDate]">
              {({ field }) => (
                <FormikDatePicker
                  {...field}
                  abbr
                  name={field.name}
                  value={field.value}
                  isOutsideRange={() => false}
                  label="Fecha de Pago"
                  placeholder="dd/mm/aaaa"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col className="top-header-green no-border-radius">
            <div className="card-header-title justify-content-center">Selección de Colaboradores</div>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={4}>
            <Field name="reliquidationProcess[filterPayrollJobManagements]">
              {({ field }) => (
                <InputSelect
                  {...field}
                  label="Área"
                  placeholder="Seleccionar Área"
                  request={fetchJobManagements}
                  value={reliquidationProcess.filterPayrollJobManagements}
                  isMulti
                  onChange={data => {
                    handleMultiChange(data, 'reliquidationProcess[filterPayrollJobManagementsIds]', true, true);
                    setFieldValue(field.name, data || []);
                  }}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  isClearable
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="reliquidationProcess[filterPayrollSubsidiaries]">
              {({ field }) => (
                <InputSelect
                  {...field}
                  label="Sucursal"
                  placeholder="Seleccionar Sucursal"
                  request={fetchSubsidiaries}
                  value={reliquidationProcess.filterPayrollSubsidiaries}
                  isMulti
                  onChange={data => {
                    handleMultiChange(data, 'reliquidationProcess[filterPayrollSubsidiariesIds]', true, true);
                    setFieldValue(field.name, data || []);
                  }}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  isClearable
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="reliquidationProcess[filterPayrollCostCenters]">
              {({ field }) => (
                <InputSelect
                  {...field}
                  value={reliquidationProcess.filterPayrollCostCenters}
                  isMulti
                  label="Centros de Costo"
                  placeholder="Seleccionar Centros de Costo"
                  request={fetchCostCenters}
                  onChange={data => {
                    handleMultiChange(data, 'reliquidationProcess[filterPayrollCostCentersIds]', true, true);
                    setFieldValue(field.name, data || []);
                  }}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={4}>
            <Field name="reliquidationProcess[filterPayrollBranchOffices]">
              {({ field }) => (
                <InputSelect
                  {...field}
                  value={reliquidationProcess.filterPayrollBranchOffices}
                  isMulti
                  label="Lugar de Prestación de Servicio"
                  placeholder="Seleccionar Lugar de Prestación"
                  request={fetchBranchOffices}
                  onChange={data => {
                    handleMultiChange(data, 'reliquidationProcess[filterPayrollBranchOfficesIds]', true, true);
                    setFieldValue(field.name, data || []);
                  }}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="reliquidationProcess[employees]">
              {({ field }) => (
                <React.Fragment key={employeesSelectKey}>
                  <InputSelect
                    {...field}
                    isMulti
                    label="Trabajador"
                    placeholder="Seleccionar Trabajador"
                    value={reliquidationProcess.employees}
                    request={fetchEmployees}
                    isOptionDisabled={handleDisabledSelection}
                    onChange={data => {
                      handleMultiChange(data, 'reliquidationProcess[employeeIds]', true);
                      setFieldValue(field.name, data || []);
                    }}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                  />
                </React.Fragment>
              )}
            </Field>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col className="top-header-green no-border-radius">
            <div className="card-header-title justify-content-center">Concepto a Reliquidar</div>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md={12}>
            <small className="text-muted">
              Seleccione el concepto y monto a reliquidar, si aplica para todo de igual forma
            </small>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md={4}>
            <Field name="reliquidationProcess[balance]">
              {({ field }) => (
                <InputSelect
                  {...field}
                  label="Concepto"
                  placeholder="Seleccionar Concepto"
                  defaultOptions={concepts}
                  request={fetchConcepts}
                  value={field.value || null}
                  onChange={data => {
                    setFieldValue(field.name, data || null);
                    setFieldValue('reliquidationProcess[balanceId]', data ? data.value : '');
                  }}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  isClearable
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="reliquidationProcess[parsedAmount]">
              {({ field }) => (
                <FormikNumber
                  {...field}
                  leftAddon="$"
                  label="Monto"
                  fieldName="reliquidationProcess[balanceAmount]"
                  setFieldValue={setFieldValue}
                  decimalScale={0}
                  errors={errors}
                  touched={touched}
                />
              )}
            </Field>
          </Col>
          <Col md={4}>
            <Field name="reliquidationProcess[advanceBalance]">
              {({ field }) => (
                <InputSelect
                  {...field}
                  label="Concepto anticipado"
                  placeholder="Seleccionar Concepto anticipado"
                  defaultOptions={advanceConcepts}
                  request={(inputValue, callback) => fetchConcepts(inputValue, callback, false)}
                  value={field.value || null}
                  onChange={data => {
                    setFieldValue(field.name, data || null);
                    setFieldValue('reliquidationProcess[advanceBalanceId]', data ? data.value : '');
                  }}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  isClearable
                />
              )}
            </Field>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          GUARDAR
        </Button>
      </Modal.Footer>
    </Form>
  );
};

const setInitialValues = props => {
  const { reliquidationProcess } = props;

  return {
    reliquidationProcess: {
      name: reliquidationProcess.name || '',
      rangeDate: [],
      startDate: reliquidationProcess.startDate || '',
      endDate: reliquidationProcess.endDate || '',
      paymentDate: reliquidationProcess.paymentDate || '',
      filterPayrollJobManagements: [],
      filterPayrollSubsidiaries: [],
      filterPayrollCostCenters: [],
      filterPayrollBranchOffices: [],
      employeeIds: [],
      employees: [],
      balanceId: '',
      balance: null,
      balanceAmount: '',
      parsedAmount: '',
      advanceBalanceId: '',
      advanceBalance: null
    }
  };
};

const validationSchema = Yup.object().shape({
  reliquidationProcess: Yup.object().shape({
    name: Yup.string().required('Debes ingresar un nombre'),
    rangeDate: Yup.array()
      .rangedate(true)
      .required('Debes ingresar un rango de fechas'),
    startDate: Yup.date()
      .formatdate()
      .required('Debes ingresar una fecha de inicio'),
    endDate: Yup.date()
      .formatdate()
      .required('Debes ingresar una fecha de término'),
    paymentDate: Yup.date()
      .formatdate()
      .required('Debes ingresar una fecha de pago'),
    employees: Yup.array().min(1, 'Debes escoger al menos un colaborador.'),
    balanceAmount: Yup.number().when('balanceId', {
      is: val => val,
      then: Yup.number()
        .positive('Debe ser mayor a 0')
        .required('Debes ingresar un monto.'),
      otherwise: Yup.number()
        .nullable()
        .transform((currentValue, originalValue) => {
          return originalValue === '' ? null : currentValue;
        })
    })
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(ReliquidationProcessFormContent);
